import { Select } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import walletApi from 'api/walletApi';
import withdrawApi from 'api/withdrawApi';
import { useAppSelector, useAppTranslation, useErrTranslation } from 'app/hooks';
import CloseIcon from 'assets/icons/CloseIcon';
import PasteIcon from 'assets/icons/PasteIcon';
import SuccessIcon from 'assets/icons/SuccessIcon';
import BigNumber from 'bignumber.js';
import { showErrorModal } from 'components/Modals/CommonModals';
import { IMG_ALT } from 'consts/app';
import Typo from 'consts/typography';
import { selectAssets, selectCoins } from 'features/home/homeSlice';
import { useHandleGetWallets } from 'hooks/useHandleGetWallets';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import useThemes from 'hooks/useThemes';
import useUserDetail from 'hooks/useUserDetail';
import { AssetResponseModel } from 'models/asset';
import { WithdrawSettingsResponseModel } from 'models/withdraw';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { platformStates } from 'redux/platformSlice';
import { selectCurrentUser } from 'redux/userSlice';
import { WITHDRAW_MEMO_CHAIN } from 'utils/commonUtils';
import { formatAmount, formatAmountV2 } from 'utils/formatAmount';
import { getLocalStorage } from 'utils/sessionStorage';

interface WithdrawModalProps {
  title?: string;
  visible: boolean;
  closeModal: () => void;
  className?: string;
}

export const isValidAmount = (amount: string): boolean => {
  if (!amount.length) return true;
  if (amount.length === 2 && amount[0] === '0' && amount[1] !== '.') return false;
  if (amount.length === 10 && amount[9] === '.') return false;
  return true;
};

export const isValidMfa = (amount: string): boolean => {
  if (!amount.length) return true;
  if (amount.length > 6) return false;
  return true;
};

interface UsdChangeType {
  amount: string;
  checked: boolean;
}

const WithdrawModal: React.FunctionComponent<WithdrawModalProps> = (props) => {
  const [withdrawSettings, setWithdrawSettings] = useState<WithdrawSettingsResponseModel>({
    id: 1,
    minWithdrawAmount: 100,
    maxWithdrawRequest24hrs: 3,
  });
  const [currentAsset, setCurrentAsset] = useState<number>(0);
  const [currentAssetInfo, setCurrentAssetInfo] = useState<AssetResponseModel | null>(null);
  const [currentCoin, setCurrentCoin] = useState<number>(0);
  const [amount, setAmount] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [mfaOtp, setMfaOtp] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowSuccess, setIsShowSuccess] = useState<boolean>(false);
  const { title = '', visible, closeModal, className = '' } = props;
  const { isPhone, isTablet } = useAppSelector(platformStates);
  const accessToken = getLocalStorage('access_token_terabet');
  const coins = useAppSelector(selectCoins);
  const assets = useAppSelector(selectAssets);
  const handleResponseError = useHandleResponseError();
  const { getWallet, userWallets } = useHandleGetWallets();
  const t = useAppTranslation();
  const et = useErrTranslation();
  const { isUserLoggedIn } = useUserDetail();
  const { currentTheme } = useThemes();
  const currentUser = useSelector(selectCurrentUser);
  const [usdChange, setUsdChange] = useState<UsdChangeType>({
    amount: '',
    checked: false,
  });
  const [rate, setRate] = useState<number>(0);
  const [toTag, setToTag] = useState<string | undefined>();

  const coinOptions = useMemo(() => {
    return coins
      .filter((x) => x.enableWithdrawal)
      .filter((x) => x.name !== 'USD')
      .map((coin) => ({
        value: coin.id,
        label: (
          <div className="flex-row items-center gap-2 h-full">
            <img src={coin.image} alt={IMG_ALT} className="dropdown-logo" />
            <span
              className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}
            >
              {coin.symbol}
            </span>
          </div>
        ),
        symbol: coin.symbol,
        minAmount: coin.minAmount,
      }));
  }, [coins, isPhone, isTablet]);

  const currentCrypto = useMemo(() => {
    return coinOptions.find((c) => c.value === currentCoin);
  }, [currentCoin, coinOptions]);

  const getPrice = useCallback(async () => {
    if (currentCrypto?.symbol) {
      const { ok, body } = await walletApi.getTokenPrice(currentCrypto?.symbol);
      if (ok && body) {
        setRate(body.price);
      }
    }
  }, [currentCoin, coinOptions, currentCrypto?.symbol]);

  useEffect(() => {
    if (visible) getPrice();
  }, [visible, getPrice]);

  const onUsdCheck = (event: ChangeEvent<HTMLInputElement>) => {
    setUsdChange((prev) => ({ ...prev, checked: !prev.checked }));
    setAmount('0');
  };

  const onUsdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^(?!.*\..*\..*)(?=.*\d)(?!.*\.\d{3,})(?!.{11,}$)[\d.]{0,10}$/;
    const value = event.target.value;

    if ((regex.test(value) && isValidAmount(value)) || value === '') {
      setUsdChange((prev) => ({ ...prev, amount: value }));
    }
  };

  const fetchData = useCallback(async () => {
    const { ok, body, error } = await withdrawApi.getWithdrawSettings();

    if (ok && body) {
      setWithdrawSettings(body);
    }

    handleResponseError(error);
  }, []);

  useEffect(() => {
    if (visible) fetchData();
  }, [visible, fetchData]);

  const networkOptions = useMemo(() => {
    return assets
      .filter((asset) => asset.coin.id === currentCoin)
      .map((asset) => ({
        value: asset.assetId,
        label: (
          <span
            className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}
          >
            {asset.chain.name}
          </span>
        ),
      }));
  }, [assets, currentCoin, isPhone, isTablet]);

  useEffect(() => {
    if (coins.length !== 0) {
      setCurrentCoin(
        coins.filter((c) => c.name !== 'USD').find((coin) => coin.enableWithdrawal)?.id || 0,
      );
    }
  }, [coins]);

  useEffect(() => {
    if (assets.length !== 0) {
      const selectAsset = assets.find((asset) => asset.coin.id === currentCoin);
      setCurrentAsset(selectAsset?.assetId || 0);
      setCurrentAssetInfo(selectAsset || null);
    }
  }, [currentCoin, assets]);

  const balanceWallet = useMemo(() => {
    return userWallets.find((w) => w.coin.id === currentCoin);
  }, [currentCoin, userWallets]);

  const onAssetChange = (event: number) => {
    setCurrentAsset(event);
    setCurrentAssetInfo(assets.find((asset) => asset.assetId === event) || null);
  };
  const onCoinChange = (event: number) => {
    setCurrentCoin(event);
  };

  const handlePasteFromClipboard = () => {
    navigator.clipboard.readText().then((data) => {
      setAddress(data);
    });
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^[0-9]*[.,]?[0-9]{0,9}$/;
    const value = event.target.value.replace(',', '.');

    if ((regex.test(value) && isValidAmount(value)) || value === '') {
      setAmount(value);
    }
  };

  const onMfaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^(?!.*\..*\..*)(?=.*\d)(?!.*\.\d{3,})(?!.{11,}$)[\d.]{0,10}$/;
    const value = event.target.value;

    if ((regex.test(value) && isValidMfa(value)) || value === '') {
      setMfaOtp(value);
    }
  };

  const onSubmit = useCallback(async () => {
    setIsLoading(true);
    if (currentAssetInfo) {
      const { ok, error } = await withdrawApi.withdraw(
        {
          assetId: currentAsset,
          amount: Number(amount),
          toAddress: address,
          mfaOtp: mfaOtp,
          toTag: WITHDRAW_MEMO_CHAIN.includes(currentAssetInfo.chain.symbol) ? toTag : undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      setIsLoading(false);
      if (ok) {
        getWallet();
        setIsShowSuccess(true);
      }

      if (error?.title?.includes('error.withdrawal.max-request-24hrs')) {
        showErrorModal({
          content: et(error.message, { times: withdrawSettings.maxWithdrawRequest24hrs }),
          title: et(error.title || '', { defaultValue: et('defaultMessage.title') }),
        });
        return;
      }

      if (
        error?.message?.includes('error.withdrawal.min-amount') ||
        error?.message?.includes('error.withdrawal.max-amount')
      ) {
        const [errorCode, amount] = error.message.split(':');
        showErrorModal({
          content: et(errorCode, {
            amount: formatAmount(amount, 8),
            symbol: currentCrypto?.symbol,
          }),
          title: et(error.title || '', {
            defaultValue: et('defaultMessage.title'),
          }),
        });
        return;
      }

      if (error) {
        handleResponseError(error);
      }
    }
  }, [amount, address, mfaOtp]);

  const onCloseModal = () => {
    resetModal();
    closeModal();
  };

  const resetModal = () => {
    setAddress('');
    setAmount('');
    setIsShowSuccess(false);
    setMfaOtp('');
    setUsdChange({
      amount: '',
      checked: false,
    });
  };

  useEffect(() => {
    if (rate) {
      const actualAmount = new BigNumber(Number(usdChange.amount)).div(rate).toString();
      setAmount(actualAmount);
    }
  }, [usdChange, rate, currentCoin]);

  const handleSetField = (value: string) => {
    if (usdChange.checked) {
      setUsdChange((prev) => ({
        ...prev,
        amount: value,
      }));
    } else {
      setAmount(value);
    }
  };

  const isDisabled = useMemo(() => {
    if (!currentAssetInfo) {
      return true;
    }
    return isLoading || !address || Number(amount) === 0 || (currentUser?.isEnableMfa && !mfaOtp);
  }, [currentAssetInfo, isLoading, address, amount, currentUser, mfaOtp]);

  return (
    <Modal
      className={className}
      title={title}
      visible={visible}
      onCancel={onCloseModal}
      footer={null}
      centered
      maskClosable={false}
      closeIcon={<CloseIcon isDarkMode={currentTheme === 'dark'} width="20" height="20" />}
      width={600}
    >
      {!isShowSuccess ? (
        <div className="center-content-col gap-3">
          <div className="common-select">
            <p className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}>
              {t('cashier.coin')}
            </p>
            <Select
              className="antd__select"
              dropdownClassName="antd__select-dropdown"
              value={currentCoin ? currentCoin : null}
              onChange={onCoinChange}
              options={coinOptions}
            />
          </div>
          <div className="common-select">
            <p className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}>
              {t('cashier.network')}
            </p>
            <Select
              className="antd__select"
              dropdownClassName="antd__select-dropdown"
              value={currentAsset ? currentAsset : null}
              onChange={onAssetChange}
              options={networkOptions}
            />
          </div>
          <div className="common-input input-address">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="withdraw-address"
            >
              {t('cashier.address')}
            </label>
            <input
              type="text"
              name="withdraw-address"
              id="withdraw-address"
              autoComplete="off"
              onChange={(event) => setAddress(event.target.value)}
              value={address}
              placeholder={t('placeholder.enterDepositAddress')}
            />
            <PasteIcon
              isDarkmode={currentTheme === 'dark'}
              className="icon-paste"
              onClick={handlePasteFromClipboard}
            />
          </div>
          {currentAssetInfo && WITHDRAW_MEMO_CHAIN.includes(currentAssetInfo.chain.symbol) && (
            <div className="common-input input-address">
              <label
                className={`${
                  isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                } font-medium primary-text-color`}
                htmlFor="withdraw-address"
              >
                Memo
              </label>
              <input
                type="text"
                name="withdraw-address"
                id="withdraw-address"
                autoComplete="off"
                onChange={(event) => setToTag(event.target.value)}
                value={toTag}
                placeholder="memo"
              />
            </div>
          )}
          <div className=" w-80 usd-checkbox">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="amount"
            >
              USD
            </label>
            <div className="flex ">
              <input
                type="checkbox"
                onChange={onUsdCheck}
                checked={usdChange.checked}
                style={{ accentColor: 'var(--textColorButtonMainTabHover)', zoom: '1.3' }}
              />
            </div>
          </div>
          {usdChange.checked ? (
            <>
              <div className="common-input input-amount w-100 ">
                <label
                  className={`${
                    isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                  } font-medium primary-text-color`}
                  htmlFor="amount"
                >
                  {t('cashier.amount')}
                </label>
                <div className="usd-to-amount-input">
                  <div className="flex-row items-center">
                    <span>$</span>
                    <input
                      type="text"
                      name="amount"
                      id="amount"
                      autoComplete="off"
                      onChange={onUsdChange}
                      value={usdChange.amount}
                      placeholder={t('placeholder.enterAmount')}
                    />
                  </div>

                  <p>{`${currentCrypto?.symbol} ${formatAmount(amount, 8)}`}</p>
                </div>
              </div>
              <div className="common-input input-amount w-100 ">
                <label
                  className={`${
                    isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                  } font-medium primary-text-color`}
                  htmlFor="amount"
                ></label>
                {/* <CustomAmountButton
                  symbol="USD"
                  onSetField={handleSetField}
                  onOptionDoubleClick={handleDoubleClickOption}
                  classes="custom-button-swap custom-button-modal"
                /> */}
              </div>
            </>
          ) : (
            <>
              <div className="common-input input-amount">
                <label
                  className={`${
                    isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                  } font-medium primary-text-color`}
                  htmlFor="amount"
                >
                  {t('cashier.amount')}
                </label>
                <input
                  type="text"
                  name="amount"
                  id="amount"
                  autoComplete="off"
                  onChange={onChange}
                  value={amount}
                  placeholder={t('placeholder.enterAmount')}
                />
              </div>
              {/* <div className="common-input input-amount w-100 ">
                <label
                  className={`${
                    isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                  } font-medium primary-text-color`}
                  htmlFor="amount"
                ></label>
                <CustomAmountButton
                  symbol={currentCrypto?.symbol}
                  onSetField={handleSetField}
                  onOptionDoubleClick={handleDoubleClickOption}
                  classes="custom-button-swap custom-button-modal"
                />
              </div> */}
            </>
          )}
          {currentUser?.isEnableMfa && (
            <>
              <div className="common-input input-amount">
                <label
                  className={`${
                    isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                  } font-medium primary-text-color`}
                  htmlFor="mfa"
                >
                  {t('cashier.MFA')}
                </label>
                <input
                  type="text"
                  name="mfa"
                  id="mfa"
                  autoComplete="off"
                  onChange={onMfaChange}
                  value={mfaOtp}
                  placeholder={t('placeholder.enterMFA')}
                />
              </div>
            </>
          )}

          {isUserLoggedIn && (
            <>
              <div className="withdraw-balance">
                <div className="withdraw-balance__left"></div>
                <div className="withdraw-balance__right">
                  <p
                    className={`${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2} font-normal`}
                  >{`${t('balance')}: ${formatAmountV2(balanceWallet?.balance ?? 0.0, 8)} ${
                    balanceWallet?.coin.symbol ?? ''
                  }`}</p>
                </div>
              </div>
              <div className="withdraw-balance">
                <div className="withdraw-balance__left"></div>
                <div className="withdraw-balance__right">
                  <button
                    disabled={isDisabled}
                    className={`button-generate w-100 ${isDisabled ? 'disabled' : ''}`}
                    type="submit"
                    onClick={onSubmit}
                  >
                    {t('withdraw.title')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="center-content-col gap-3 withdraw--success">
          <SuccessIcon width="40" height="40" />
          <p className="title">{t('withdraw.success.title')}</p>
          <p className="message">{`$${amount} ${t('withdraw.success.message')}`}</p>
          <button type="button" className="continue" onClick={onCloseModal}>
            {t('withdraw.close')}
          </button>
        </div>
      )}
    </Modal>
  );
};

export default WithdrawModal;
